<template>
	<div class="custom-searchbar">
		<div class="searchbar-input-container">
			<FontAwesomeIcon :icon="faSearch" class="search-icon" />
			<input
				ref="inputRef"
				type="search"
				class="searchbar-input"
				:placeholder="placeholder"
				:value="modelValue"
				:autocorrect="autocorrect"
				:autocomplete="autocomplete"
				@input="handleInput"
				@blur="handleBlur"
			/>
			<button v-if="modelValue" class="searchbar-clear-button" @click="handleClear" type="button">
				<FontAwesomeIcon :icon="faTimes" />
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount } from 'vue';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';

const props = withDefaults(
	defineProps<{
		modelValue: string;
		placeholder?: string;
		debounce?: number;
		autocorrect?: string;
		autocomplete?: string;
	}>(),
	{
		placeholder: 'Search',
		debounce: 0,
		autocorrect: 'off',
		autocomplete: 'off',
	}
);

const emit = defineEmits<{
	(e: 'update:modelValue', value: string): void;
	(e: 'onChange', event: { target: { value: string } }): void;
	(e: 'onClear'): void;
	(e: 'onBlur'): void;
}>();

const inputRef = ref<HTMLInputElement | null>(null);
let debounceTimer: NodeJS.Timeout | null = null;

function handleInput(event: Event) {
	const target = event.target as HTMLInputElement;
	const value = target.value;

	emit('update:modelValue', value);

	// Clear existing timer
	if (debounceTimer) {
		clearTimeout(debounceTimer);
	}

	// Set new timer if debounce is specified
	if (props.debounce > 0) {
		debounceTimer = setTimeout(() => {
			emit('onChange', { target: { value } });
		}, props.debounce);
	} else {
		emit('onChange', { target: { value } });
	}
}

function handleClear() {
	if (debounceTimer) {
		clearTimeout(debounceTimer);
		debounceTimer = null;
	}

	emit('update:modelValue', '');
	emit('onClear');
	emit('onChange', { target: { value: '' } });
	inputRef.value?.focus();
}

function handleBlur() {
	emit('onBlur');
}

function setFocus() {
	inputRef.value?.focus();
}

onBeforeUnmount(() => {
	if (debounceTimer) {
		clearTimeout(debounceTimer);
		debounceTimer = null;
	}
});

defineExpose({
	setFocus,
});
</script>

<style lang="scss" scoped>
.custom-searchbar {
	width: 100%;
	padding: 8px;
	background: #10161d;
}

.searchbar-input-container {
	position: relative;
	display: flex;
	align-items: center;
	border-radius: 8px;
}

.search-icon {
	color: rgba(255, 255, 255, 0.6);
	font-size: 16px;
	margin: 0 18px;
	flex-shrink: 0;
}

.searchbar-input {
	flex: 1;
	background: transparent;
	border: none;
	outline: none;
	color: #ffffff;
	font-size: 16px;
	padding: 12px 8px;
	font-family: inherit;

	&::placeholder {
		color: rgba(255, 255, 255, 0.5);
	}

	&::-webkit-search-cancel-button,
	&::-webkit-search-decoration {
		-webkit-appearance: none;
		appearance: none;
	}
}

.searchbar-clear-button {
	background: transparent;
	border: none;
	outline: none;
	cursor: pointer;
	padding: 4px;
	margin-right: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: rgba(255, 255, 255, 0.6);
	font-size: 16px;
	flex-shrink: 0;
	transition: color 0.2s ease;

	&:hover {
		color: rgba(255, 255, 255, 0.9);
	}
}
</style>
